/**
 * Defines the UI reducer.
 */
import { get, set, reject, forEach, find, isEmpty, assignIn } from 'lodash';
import {
  IAction,
  IDropdownMenuOption,
  IErrorPageType,
  IMenuItem,
  INotificationBoxItem,
  IModalDialogOptions,
  IModalDialogResult,
  IUserConfig,
  ToolbarButtonDefinition,
} from '@shared/types';
import {
  BULK_UPDATE,
  BULK_UPDATE_ERROR,
  BULK_UPDATE_SUCCESS,
  FETCH_GET_DOCUMENT_BY_ID,
  FETCH_GET_DOCUMENT_BY_ID_ERROR,
  FETCH_GET_DOCUMENT_BY_ID_SUCCESS,
  FETCH_LIST,
  FETCH_LIST_ERROR,
  FETCH_LIST_SUCCESS,
  FETCH_SCHEMA,
  FETCH_SCHEMA_ERROR,
  FETCH_SCHEMA_SUCCESS,
  FETCH_CREATE_ENTITY,
  FETCH_CREATE_ENTITY_ERROR,
  FETCH_CREATE_ENTITY_SUCCESS,
  FETCH_UPDATE_ENTITY,
  FETCH_UPDATE_ENTITY_ERROR,
  FETCH_UPDATE_ENTITY_SUCCESS,
  FORM_VALUE_CHANGED,
  OPEN_ROUTE_ACTION,
  DELETE_DOCUMENT_BY_ENTITY_ID,
  DELETE_DOCUMENT_BY_ENTITY_ID_SUCCESS,
  DELETE_DOCUMENT_BY_ENTITY_ID_ERROR,
  XprtDataActions,
  SessionActions,
  UiActions,
} from '@store/actions';
import { applySubReducer } from '../base-reducer';
import {
  LIST_INITIAL_STATE,
  uiListReducer,
  IUIListState,
} from './ui.list.reducer';
import { PreferencesActions } from '@app/store/actions/preferences/preferences.actions';

export const FETCH_SCHEME_USED_ON_FIRST_RUN: string =
  'FETCH_SCHEME_USED_ON_FIRST_RUN';
export const FETCH_SCHEME_USED_ON_FIRST_RUN_SUCCESS: string =
  'FETCH_SCHEME_USED_ON_FIRST_RUN_SUCCESS';
export const FETCH_SCHEME_USED_ON_FIRST_RUN_ERROR: string =
  'FETCH_SCHEME_USED_ON_FIRST_RUN_ERROR';
export interface ISaveStatusType {
  isSaveInProgress: boolean;
  isErrorState: boolean;
}

export interface IProgressBarLoader {
  showBar?: boolean;
  total?: number;
  current?: number;
  text?: string;
}

export interface IUiState {
  loader: boolean;
  modalLoader: boolean;
  reloadListConfig: number;
  pendingRequests: number;
  showNavbar: boolean;
  hiddenMenu?: boolean;
  isShowStoresModal?: boolean;
  isDeleteViewMode?: boolean;
  storesModalOptions?: any;
  menuItems?: IMenuItem[];
  selectedMenuItem?: IMenuItem;
  genericModalVisible?: boolean;

  dropdownMenuOptions?: IDropdownMenuOption[];
  dropdownMenuPositionX?: number;
  dropdownMenuPositionY?: number;
  dropdownMenuDirectionStyles?: {
    yStyle?: string;
    xStyle?: string;
    wrapperClass?: string;
  };
  dropdownMenuVisible?: boolean;
  errorPage?: IErrorPageType;
  list?: IUIListState;
  editor?: {
    entityType?: string;
    formValue?: any;
    nonPatchedFormValue?: any;
  };
  theme?: string;
  formModal?: IModalDialogOptions;
  formModalValue?: any;
  formModalResult?: IModalDialogResult;
  bufferedDisplayedDocuments?: {
    [entityType: string]: {
      [entityField: string]: {
        [entityId: string]: any[];
      };
    };
  };

  entityItemsRequestCondition?: {
    [entityType: string]: {
      [key: string]: {
        isPending: boolean;
      };
    };
  };
  notificationBox: INotificationBoxItem[];
  documentValueChanges: [];
  saveStatusState: ISaveStatusType;
  gridRowLoader: boolean;
  isEditPackageChangesMode: boolean;
  isBatchRequestLoading: boolean;
  isPreventLoaderCloseAfterRequestFinish: boolean;
  userConfig?: IUserConfig[];
  progressBarLoader: IProgressBarLoader;
  breadcrumb?: {
    buttons?: ToolbarButtonDefinition[];
  };
}

export const INITIAL_STATE: IUiState = {
  loader: false,
  modalLoader: false,
  reloadListConfig: 0,
  pendingRequests: 0,
  showNavbar: true,
  isShowStoresModal: false,
  isDeleteViewMode: false,
  storesModalOptions: {
    forceSelect: false,
    masterList: true,
  },
  menuItems: null,
  selectedMenuItem: null,
  dropdownMenuOptions: [],
  dropdownMenuPositionX: 0,
  dropdownMenuPositionY: 0,
  dropdownMenuDirectionStyles: {
    yStyle: 'bottom',
    xStyle: 'right',
    wrapperClass: '',
  },
  dropdownMenuVisible: false,
  hiddenMenu: false,
  errorPage: {},
  list: LIST_INITIAL_STATE,
  theme: '',
  formModal: {},
  formModalValue: {},
  formModalResult: {},
  bufferedDisplayedDocuments: {},
  entityItemsRequestCondition: {},
  documentValueChanges: [],
  notificationBox: [],
  saveStatusState: {
    isSaveInProgress: false,
    isErrorState: false,
  },
  gridRowLoader: false,
  isEditPackageChangesMode: false,
  isBatchRequestLoading: false,
  isPreventLoaderCloseAfterRequestFinish: false,
  progressBarLoader: {
    showBar: false,
    total: 0,
    current: 0,
    text: '',
  },
  breadcrumb: {
    buttons: [],
  },
};

// eslint-disable-next-line complexity
export const uiReducer = (
  inputState: IUiState = INITIAL_STATE,
  action: IAction,
): IUiState => {
  let state: IUiState = applySubReducer(
    inputState,
    'list',
    uiListReducer,
    action,
  );
  const { payload } = action;
  // eslint-disable-next-line
  switch (action.type) {
    case OPEN_ROUTE_ACTION:
      return Object.assign({}, state, {
        editor: {
          ...payload,
          documentIds: get(payload, 'options.documentIds', []),
        },
      });
    case UiActions.CLEAR_DUPLICATE_OPTION:
      return Object.assign({}, state, {
        editor: {
          options: {
            ...get(state, 'editor.options', {}),
            duplicateOption: null,
          },
        },
      });

    case UiActions.ENTITY_ITEM_REQUEST_PENDING:
      const path = [
        'entityItemsRequestCondition',
        payload.entityType,
        payload.entityIdKey,
      ];
      const currentPendingInformation = get(state, path, {});
      const pendingInformation = {
        ...currentPendingInformation,
        isPending: payload.status,
      };

      if (payload.status === false) {
        return set({ ...state }, path, pendingInformation);
      }

      return set({ ...state }, path, pendingInformation);
    case FORM_VALUE_CHANGED:
      return Object.assign({}, state, {
        editor: {
          ...state.editor,
          ...payload,
        },
      });

    case UiActions.SAVE_NON_PATCHED_FORM_VALUE:
      return Object.assign({}, state, {
        editor: {
          ...state.editor,
          nonPatchedFormValue: payload,
        },
      });

    case UiActions.CLEAR_NON_PATCHED_FORM_VALUE:
      return Object.assign({}, state, {
        editor: {
          ...state.editor,
          nonPatchedFormValue: null,
        },
      });

    case UiActions.SHOW_NAVBAR:
      return Object.assign({}, state, {
        showNavbar: true,
      });

    case UiActions.HIDE_NAVBAR:
      return Object.assign({}, state, {
        showNavbar: false,
      });

    // Store selector Modal

    case UiActions.SET_FORM_MODE:
      return Object.assign({}, state, {
        formMode: payload.formMode,
      });

    case UiActions.SELECT_SIDEBAR_ITEM:
      return Object.assign({}, state, { selectedMenuItem: payload });

    case UiActions.SHOW_GENERIC_MODAL:
      return Object.assign({}, state, {
        genericModalVisible: true,
        genericModalResult: null,
        genericModalOptions: Object.assign(
          { modalResult: null, value: null },
          payload,
        ),
      });

    case UiActions.HIDE_GENERIC_MODAL:
      return Object.assign({}, state, {
        genericModalVisible: false,
        genericModalResult: payload,
      });

    case UiActions.SHOW_FORM_MODAL:
      return Object.assign({}, state, {
        formModal: Object.assign({}, payload, {
          visible: true,
        }),
      });

    case UiActions.HIDE_FORM_MODAL:
      return Object.assign({}, state, {
        formModalResult: payload || {},
        formModal: {
          modalId: get(payload, 'modalId', state.formModal.modalId),
          visible: false,
        },
      });
    case UiActions.STORE_VALUE_BY_MODAL_ID:
      return Object.assign({}, state, {
        formModalValue: {
          ...state.formModalValue,
          [get(payload, 'modalId', '')]: get(payload, 'value', {}),
        },
      });
    case UiActions.SHOW_DROPDOWN_MENU:
      return Object.assign({}, state, {
        dropdownMenuVisible: true,
        dropdownMenuResult: null,
        dropdownMenuOptions: payload.options,
        dropdownMenuPositionX: get(payload, 'x', state.dropdownMenuPositionX),
        dropdownMenuPositionY: get(payload, 'y', state.dropdownMenuPositionY),
        dropdownMenuDirectionStyles: get(payload, 'directionCss', {}),
      });

    case UiActions.HIDE_DROPDOWN_MENU:
      return Object.assign({}, state, {
        dropdownMenuVisible: false,
        dropdownMenuResult: payload,
      });
    case UiActions.HIDE_MENUITEMS:
      return Object.assign({}, state, {
        hiddenMenu: payload.hidden,
      });
    case UiActions.SAVE_PRINT_COLUMNS:
      return Object.assign({}, state, {
        ...payload,
      });
    case SessionActions.FETCH_ACCESS_TOKEN:
    case SessionActions.FETCH_PERSONAL_TOKEN:
    case SessionActions.FETCH_USER_INFO:
    case XprtDataActions.FETCH_COMPANIES:
    case XprtDataActions.FETCH_STORES:
    case FETCH_SCHEMA:
    case FETCH_LIST:
    case FETCH_CREATE_ENTITY:
    case FETCH_UPDATE_ENTITY:
    case FETCH_GET_DOCUMENT_BY_ID:
    case DELETE_DOCUMENT_BY_ENTITY_ID:
    case FETCH_SCHEME_USED_ON_FIRST_RUN:
    case BULK_UPDATE:
    case UiActions.FORM_BUILDING_STARTED:
    case PreferencesActions.FETCH_PREFERENCES_FOR_COMPANY:
    case SessionActions.FETCH_TRANSLATIONS:
      return payload.options && payload.options.noLoader
        ? state
        : Object.assign({}, state, {
            // eslint-disable-next-line
            pendingRequests: state.pendingRequests + 1,
            // eslint-disable-next-line
            loader: true,
            // eslint-disable-next-line
          });

    case FETCH_CREATE_ENTITY_SUCCESS:
    case FETCH_UPDATE_ENTITY_SUCCESS:
      if (payload.entity_type) {
        state = {
          ...state,
          bufferedDisplayedDocuments: {
            ...state.bufferedDisplayedDocuments,
            [payload.entity_type]: {},
          },
        };
      }
    // falls through
    case SessionActions.FETCH_ACCESS_TOKEN_SUCCESS:
    case SessionActions.FETCH_ACCESS_TOKEN_ERROR:
    case SessionActions.FETCH_PERSONAL_TOKEN_SUCCESS:
    case SessionActions.FETCH_PERSONAL_TOKEN_ERROR:
    case SessionActions.FETCH_USER_INFO_SUCCESS:
    case SessionActions.FETCH_USER_INFO_ERROR:
    case XprtDataActions.FETCH_COMPANIES_SUCCESS:
    case XprtDataActions.FETCH_COMPANIES_ERROR:
    case FETCH_SCHEMA_SUCCESS:
    case FETCH_SCHEMA_ERROR:
    case FETCH_LIST_SUCCESS:
    case FETCH_LIST_ERROR:
    case FETCH_CREATE_ENTITY_ERROR:
    case FETCH_UPDATE_ENTITY_ERROR:
    case FETCH_GET_DOCUMENT_BY_ID_SUCCESS:
    case FETCH_GET_DOCUMENT_BY_ID_ERROR:
    case DELETE_DOCUMENT_BY_ENTITY_ID_SUCCESS:
    case DELETE_DOCUMENT_BY_ENTITY_ID_ERROR:
    case FETCH_SCHEME_USED_ON_FIRST_RUN_SUCCESS:
    case FETCH_SCHEME_USED_ON_FIRST_RUN_ERROR:
    case BULK_UPDATE_SUCCESS:
    case BULK_UPDATE_ERROR:
    case UiActions.FORM_BUILDING_COMPLETE:
    case PreferencesActions.FETCH_PREFERENCES_FOR_COMPANY_SUCCESS:
    case PreferencesActions.FETCH_PREFERENCES_FOR_COMPANY_ERROR:
    case SessionActions.SET_TRANSLATION_INITIALIZED:
      const pendingRequests: number =
        state.pendingRequests - 1 < 0 ? 0 : state.pendingRequests - 1;
      if (state.isPreventLoaderCloseAfterRequestFinish) {
        return Object.assign({}, state, {
          pendingRequests,
        });
      }
      if (payload.options && payload.options.noLoader) {
        return state;
      }
      {
        return Object.assign({}, state, {
          pendingRequests,
          loader: !!pendingRequests,
        });
      }

    case UiActions.UPDATE_LOADER:
      return {
        ...state,
        loader: payload.loader,
        progressBarLoader: payload.loader
          ? {
              ...state.progressBarLoader,
              ...payload.progressBarLoader,
            }
          : null,
      };

    case UiActions.UPDATE_MODAL_LOADER:
      return Object.assign({}, state, { modalLoader: payload.modalLoader });

    case UiActions.SHOW_ERROR_PAGE_ACTION:
      return Object.assign({}, state, {
        errorPage: payload.errorPage,
      });

    case UiActions.UPDATE_SIDEBAR_ITEMS:
      return Object.assign({}, state, {
        menuItems: payload.items,
      });

    case UiActions.STORE_ENTITY_LIST_FILTER_TEXT:
      return Object.assign({}, state, {
        listFilterText: payload.text,
        listFilterEntityType: payload.entity_type,
      });

    case UiActions.SET_UI_THEME:
      return Object.assign({}, state, {
        theme: payload.theme,
      });

    case UiActions.ADD_NOTIFICATION_TO_THE_BOX:
      return { ...state, notificationBox: [...state.notificationBox, payload] };

    case UiActions.REMOVE_NOTIFICATION_FROM_THE_BOX:
      return {
        ...state,
        notificationBox: <INotificationBoxItem[]>(
          reject(state.notificationBox, { id: payload.notificationId })
        ),
      };

    case UiActions.REMOVE_ALL_NOTIFICATION_FROM_THE_BOX:
      return {
        ...state,
        notificationBox: [],
      };

    case UiActions.REMOVE_NOTIFICATION_ON_PAGE_LEAVE_FROM_THE_BOX:
      return {
        ...state,
        notificationBox: reject(state.notificationBox, {
          isNeedToRemoveOnPageLeave: true,
        }),
      };

    case UiActions.UPDATE_DOCUMENT_VALUES:
      return Object.assign({}, state, {
        documentValueChanges: payload || [],
      });

    case UiActions.SET_GRID_ROW_LOADER:
      return {
        ...state,
        gridRowLoader: payload?.gridRowLoader ?? false,
      };

    case UiActions.SET_IS_DELETED_VIEW_MODE:
      return {
        ...state,
        isDeleteViewMode: payload?.flag ?? false,
      };
    case UiActions.KEEP_BATCH_REQUEST_LOADING:
      return {
        ...state,
        isBatchRequestLoading: payload?.isBatchRequestLoading ?? false,
      };
    case UiActions.RELOAD_LIST_CONFIG:
      return {
        ...state,
        reloadListConfig: payload?.reloadListConfig,
      };
    case UiActions.UPDATE_PREVENT_DISABLE_LOADER_STATUS:
      return {
        ...state,
        isPreventLoaderCloseAfterRequestFinish:
          payload?.isPreventLoaderCloseAfterRequestFinish,
      };
    case UiActions.FETCH_USER_CONFIG_SUCCESS:
      return {
        ...state,
        userConfig: payload?.userConfig,
      };
    case UiActions.UPDATE_USER_CONFIG:
      const userConfigs = [...state.userConfig];

      forEach(payload.userConfig as IUserConfig[], (userConfig) => {
        const oldUserConfig = find(userConfigs, {
          module_type: userConfig.module_type,
          module_name: userConfig.module_name,
        });

        if (!isEmpty(oldUserConfig)) {
          assignIn(oldUserConfig, userConfig);
        } else {
          userConfigs.push(userConfig);
        }
      });

      return {
        ...state,
        userConfig: userConfigs,
      };
    case UiActions.UPDATE_BREADCRUMB_BUTTONS:
      return {
        ...state,
        breadcrumb: {
          ...(state?.breadcrumb ?? {}),
          buttons: payload ?? [],
        },
      };
    default:
      return state;
  }
};
